import React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { ByFileModuleDTO } from 'root/apps/ModuleCompareUI/ModuleCompare.types';
import { parseModuleCSV, parseModuleJSON } from 'root/apps/ModuleCompareUI/utils/dataUtils';

type DragAndDropImportProps = {
  onFileParsed: (data: ByFileModuleDTO) => void; // Callback to pass the transformed data
  disabled?: boolean;
};

const DragAndDropImport: React.FC<DragAndDropImportProps> = ({
  onFileParsed,
  disabled = false,
}) => {
  const [error, setError] = React.useState<string | null>(null);

  const handleFileRead = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      setError('No file selected. Please try again.');
      return;
    }
    const file = files[0];
    if (!file.type.startsWith('text/') && !file.type.startsWith('application/json')) {
      setError('Unsupported file type. Please upload a CSV or JSON file.');
      return;
    }

    try {
      const fileName = file.name;
      const fileContent = await file.text();
      onFileParsed({
        fileName,
        ...(file.type.startsWith('application/json')
          ? parseModuleJSON(fileContent)
          : parseModuleCSV(fileContent)),
      });
      setError(null);
    } catch (err) {
      console.error('Error parsing file:', err);
      setError('Failed to parse the file. Please ensure it is a valid CSV or JSON file.');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    handleFileRead(files);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const files = event.target.files;
    handleFileRead(files);
  };

  return (
    <Flex flexDirection="column" alignItems="start" mt={2}>
      <Box
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: disabled ? '2px dashed #ddd' : '2px dashed #ccc',
          padding: '20px',
          width: '300px',
          textAlign: 'center',
          borderRadius: '8px',
          position: 'relative',
          backgroundColor: disabled ? '#f5f5f5' : '#f9f9f9',
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Text color={disabled ? 'gray' : 'black'}>
          {disabled ? 'Drag and drop is disabled while comparing' : 'Drag and drop your file here'}
        </Text>
        <Text size="small" color="gray-300" mt={2}>
          {/* {disabled ? '' : '(or click to upload)'} */}
          <Button variant="secondary" fill disabled={disabled} size="small">
            or click to upload
          </Button>
        </Text>
        <input
          type="file"
          disabled={disabled}
          style={{
            opacity: 0,
            position: 'absolute',
            width: '300px',
            height: '100px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            top: 0,
            left: 0,
          }}
          onChange={handleFileInputChange}
        />
      </Box>
      {error && (
        <Text mt={4} color="red">
          Error: {error}
        </Text>
      )}
    </Flex>
  );
};

export default DragAndDropImport;
